import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const weightedExitSkill: Skill = {
    name: SKILL_NAMES.weightedExit,
    kind: 'codeo',
    description:
        'Send an element out of frame the way a physical object would leave — accelerating away, ' +
        'rotating slightly, with the frame reacting — instead of fading it to nothing.',
    instructions: `# Weighted Exit

An element leaves by accelerating out of the frame, tipping slightly as it goes, while whatever it was sitting next to reacts to its absence. Almost every generated graphic exits by fading opacity to zero, which is the tell: real objects go *somewhere*.

It matters most in a phased graphic, where each phase has to clear before the next arrives. A phase that dissolves reads as a slideshow; a phase that is thrown out reads as the frame rearranging.

## Markup

\`\`\`html
<div id="exit-stage" style="position: absolute; inset: 0; overflow: hidden;">
  <div id="leaving-card" style="position: absolute; left: 260px; top: 340px; width: 620px; height: 400px; border-radius: 26px; padding: 44px; background: var(--l5-color-1, #101820); font-family: var(--l5-font-0), sans-serif; font-size: 62px; font-weight: 800; color: var(--l5-color-0, #ffffff);">Old way</div>
  <div id="arriving-card" style="position: absolute; left: 1040px; top: 340px; width: 620px; height: 400px; border-radius: 26px; padding: 44px; background: var(--l5-color-2, #38bdf8); font-family: var(--l5-font-0), sans-serif; font-size: 62px; font-weight: 800; color: var(--l5-color-1, #101820);">New way</div>
</div>
\`\`\`

## Timeline code

\`power2.in\` is the shape of leaving: slow to start, fastest at the edge of frame. The opposite of an entrance.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const LEAVE = 2.2;

gsap.set('#arriving-card', { x: 40, opacity: 0 });

// It goes somewhere: down and out, tipping as it falls.
tl.to('#leaving-card', {
    y: 900,
    rotation: 9,
    duration: 0.6,
    ease: 'power2.in',
}, LEAVE);

// The survivor reacts to the space that opened up.
tl.to('#arriving-card', { x: 0, opacity: 1, duration: 0.55, ease: 'power3.out' }, LEAVE + 0.18);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Direction carries meaning**: down and out reads as discarded; up and out reads as released or completed; sideways reads as making way for what follows.
- **Rotation**: 6–12° of tip as it leaves. Without any rotation the exit reads as mechanical; past about 20° it reads as comic.
- **Scale away**: shrinking as it exits reads as receding into depth rather than falling out of frame — quieter, and better for a serious piece.
- **The reaction is required**: something must respond to the departure. Without it the exit is just an element leaving, not the frame rearranging.
- **Stagger a group**: exiting several items 0.06s apart, in the order they arrived, reads as a set being cleared rather than a block vanishing.
- **Handoff instead**: when the leaving element should *become* the next one rather than depart, use \`${SKILL_NAMES.elementMorphHandoff}\`.

## Pitfalls

- The card is transform-tweened on several channels, so it must not carry a CSS \`transform\`.
- An element travelling out of frame needs \`data-l5-allow-offscreen\`, or the stage needs \`overflow: hidden\` to clip it.
- Use \`power2.in\` or \`power3.in\`. An \`out\` ease decelerates at the frame edge, which reads as the element getting stuck.
- Don't fade *and* throw. If it is leaving the frame, opacity is unnecessary and the fade undercuts the physicality.
- The exit must complete before the next phase needs the space. Overlapping by more than about 0.2s makes two things read at once.
- Don't exit the last element of the graphic this way — the scene would end on an empty frame. See \`${SKILL_NAMES.sustainedSceneReveal}\` for pacing the tail.
- Keep exits under 0.7s. A long departure is dead time, and the viewer has already moved on.`,
};
